/* eslint-disable eslint-comments/disable-enable-pair -- conflicting eslint errors */
/* eslint-disable @typescript-eslint/non-nullable-type-assertion-style -- conflicting eslint errors */
import "./shipment-pickup-details.css";

import { useContext, useState } from "react";

import { useAddressBook } from "~/features/address-book/hooks/use-address-book.ts";
import { useApplyAddressBookLocation } from "~/features/quote/hooks/use-apply-address-book-location.ts";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { buildAddressBookLocationFilter } from "~/features/quote/utils/build-address-book-location-filter.ts";
import { AddressBookModal } from "~/shared/components/address-book-modal/address-book-modal.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type {
	AddressBookCreateBodyV2,
	AddressBookItemSummary,
} from "~/shared/schemas/address-book-schema.ts";
import type { Holiday } from "~/shared/schemas/holiday-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import { hasContactDetailData } from "~/shared/utils/address-book-detail.ts";

import type { SelectedAddressBookItems } from "../../../../context/quote-address-context.tsx";
import { useQuoteAddressContext } from "../../../../context/quote-address-context.tsx";
import { QuoteDetailsContext } from "../../../../context/quote-details-context.tsx";
import { ActionButtonsRow } from "./components/action-buttons-row.tsx";
import { AddressRow } from "./components/address-row.tsx";
import { CityStateZipRow } from "./components/city-state-zip-row.tsx";
import { ContactRow } from "./components/contact-row.tsx";
import { HoursAndNumberRow } from "./components/hours-and-number-row.tsx";
import { NameAndDateRow } from "./components/name-and-date-row.tsx";

type ShipmentPickupDetailsProps = {
	readonly hasFieldError: (field: string) => boolean;
	readonly holidays?: Holiday[];
	readonly isSaving?: boolean;
	readonly onClearFieldError: (field: string) => void;
	readonly onSavingStateChange?: (isSaving: boolean) => void;
	readonly type: SubGroupKey;
};

export const ShipmentPickupDetails = ({
	hasFieldError,
	holidays,
	onClearFieldError,
	type,
}: ShipmentPickupDetailsProps) => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { createAddressBookItem, isCreating } = useAddressBook();
	const { showError } = useNotificationActions();
	const { selectedAddressBookItems } = useQuoteAddressContext();
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const { applyLocation, clearInvalidCountryAddress, invalidCountryAddress } =
		useApplyAddressBookLocation(type, pageData?.countries ?? []);
	const addressBookItem = selectedAddressBookItems[
		type as keyof SelectedAddressBookItems
	] as AddressBookItemSummary;

	const [showAddressBookModal, setShowAddressBookModal] = useState(false);

	useAutoSpinner(isCreating);

	const onToggleAddressBookModal = () => {
		setShowAddressBookModal(!showAddressBookModal);
	};

	const handleSaveAddressClick = () => {
		if (!addressBookItem) return;

		const isPickup = type === "pickup";
		const location = isPickup
			? quoteState.pickupPostalCode
			: quoteState.deliveryPostalCode;

		const body: AddressBookCreateBodyV2 = {
			postalCode: addressBookItem.postalCode || location.postalCode || "",
			country:
				addressBookItem.country || location.city?.state?.countryCode || "",
			city: addressBookItem.city || location.city?.name || "",
			customerId: addressBookItem.customerId || quoteState.customer.id,
			createdByCustomerId:
				addressBookItem.createdByCustomerId ?? quoteState.customer.id,
			address1: addressBookItem.address1,
			address2: addressBookItem.address2 || undefined,
			companyName: addressBookItem.companyName || "",
			stateProvince:
				addressBookItem.stateProvince ||
				location.city?.state?.abbreviation ||
				"",
			details:
				addressBookItem.details
					?.filter((detail) => {
						const isCurrentType =
							(type === "pickup" && detail.stopType === 1) ||
							(type === "delivery" && detail.stopType === 2);

						if (isCurrentType) return true;

						return hasContactDetailData(detail);
					})
					.map((detail) => ({
						...detail,
						contactName: detail.contactName ?? undefined,
						contactPhone: detail.contactPhone ?? undefined,
						hoursOpen: detail.hoursOpen ?? undefined,
						hoursClose: detail.hoursClose ?? undefined,
						timeZone: detail.timeZone ?? undefined,
						specialInstructions: detail.specialInstructions ?? "",
						accessorials: detail.accessorials ?? undefined,
					})) ?? [],
		};

		const requiredFields: readonly [string, string][] = [
			["Company Name", body.companyName],
			["Address Line 1", body.address1],
			["Postal Code", body.postalCode],
			["City", body.city],
			["State", body.stateProvince],
			["Country", body.country],
		];
		const missingFields = requiredFields
			.filter(([, value]) => !value?.trim())
			.map(([label]) => label);

		if (missingFields.length > 0) {
			showError(
				`Cannot save address. Please provide: ${missingFields.join(", ")}.`,
			);
			return;
		}

		createAddressBookItem(body);
	};

	return (
		<>
			{showAddressBookModal ? (
				<AddressBookModal
					customerId={quoteState.customer?.id ?? 0}
					locationFilter={buildAddressBookLocationFilter(
						quoteState,
						type,
						addressBookItem,
					)}
					name={type}
					onClose={onToggleAddressBookModal}
					onSelect={applyLocation}
					useLocationFilter={true}
				/>
			) : null}
			{invalidCountryAddress ? (
				<TraxModal
					cancelText={"Close"}
					onCancel={clearInvalidCountryAddress}
					onClose={clearInvalidCountryAddress}
					showConfirmButton={false}
					showConfirmationButtons={true}
					title={"Invalid Country Code"}
					type={"error"}
				>
					<p>
						This address book item has a country code of &quot;
						{invalidCountryAddress.country}&quot; that cannot be recognized as a
						valid country. Please contact TQL Tech Support at{" "}
						<strong>(800) 580-3101 ext. 56400</strong> to have this record
						corrected.
					</p>
				</TraxModal>
			) : null}

			<div className={"shipment-pickup-details"}>
				<NameAndDateRow
					hasFieldError={hasFieldError}
					holidays={holidays}
					onClearFieldError={onClearFieldError}
					type={type}
				/>

				<AddressRow
					hasFieldError={hasFieldError}
					onClearFieldError={onClearFieldError}
					onToggleAddressBookModal={onToggleAddressBookModal}
					type={type}
				/>

				<CityStateZipRow type={type} />

				<ContactRow
					hasFieldError={hasFieldError}
					onClearFieldError={onClearFieldError}
					type={type}
				/>

				<HoursAndNumberRow
					hasFieldError={hasFieldError}
					onClearFieldError={onClearFieldError}
					type={type}
				/>
			</div>

			<ActionButtonsRow
				isSaving={isCreating}
				onSaveAddress={handleSaveAddressClick}
			/>
		</>
	);
};
